import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios, ensureAbsoluteUrl } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { euro, MOTIVI_SCARTO, statoImmobile } from '../../utils/scheda';
import { dominio, formattaRoma, giornoEMese, ilGiorno, indirizzoCompleto, leggiDataUtc, linkChiama, linkIndicazioni, titoloImmobile } from '../../utils/percorso';
import { CAMPI_COMPILA } from '../../utils/compila';
import { altriPortali, fotoImmobile, formatEuroMq, prezzoMq, prezzoNumero } from '../../utils/immobili';
import { ritirato, testoRitirato, venduto, testoVenduto } from '../../utils/prezzoZona';
import { indicazioni, rigaVisita, visitaDa } from '../../utils/visite';
import Icona from '../../components/cliente/Icone';
import GalleriaFoto from '../../components/features/GalleriaFoto';
import { DatiImmobile, Dintorni } from '../../components/cliente/immobili/DatoImmobile';
import { Iniziale } from '../../components/staff/Persone';
import EsitoTelefonata from '../../components/staff/EsitoTelefonata';
import FissaVisita from '../../components/staff/FissaVisita';
import AzioniVisita from '../../components/staff/AzioniVisita';
import Eliminazione from '../../components/staff/Eliminazione';
import { linkImmobileInScheda } from '../../components/staff/CosaVoce';
import CompilaDati from '../../components/scheda/CompilaDati';
import { CambiaStato } from '../../components/scheda/TabImmobili';
import {
  AgenziaImmobile, NoteImmobile, ParereStaff, PrezzoZonaImmobile, ProponiAdAltri, PropostaImmobile, SeLaCompra, StoriaStaff, VisiteImmobile,
} from '../../components/immobile/Riquadri';
import '../../components/staff/scheda.css';
import '../../components/staff/impostazioni.css';
import '../../components/staff/scheda-immobile.css';
import { messaggioErrore } from '../../utils/agenda';

// /immobili/:id per admin e collaboratori (il cliente ha SchedaImmobileCliente): la stessa struttura della scheda del
// cliente — galleria, titolo, dati in badge, prezzo e €/m², dintorni, parere, visite con foto, storia, agenzia, "se la
// compra" — più la parte dello staff: il cliente in testa (nome, fase, chi lo segue), stato a menu, Esito telefonata,
// Fissa / Sposta / Annulla visita, "Compila dati" e "Riprova la lettura" (solo per gli annunci che l'estrattore non
// ha letto), note interne, commento sull'agenzia, prezzo con lo scostamento di zona, "Proponi ad altri clienti",
// Elimina (admin). Il collaboratore apre solo gli immobili dei clienti che segue: il server risponde 403.

const ORIGINE = { cliente: 'dal cliente', admin: 'dallo staff', collaboratore: 'dallo staff' };

// «Letto dall'estrattore il 21/09 alle 14:32 da immobiliare.it» e, sotto "dettagli lettura", da dove vengono i dati.
// Niente riga per le case scritte a mano: il server manda lettura: null (backend/scheda_immobile.py).
const NOME_CAMPO = Object.fromEntries(CAMPI_COMPILA.map(([campo, etichetta]) => [campo, etichetta.replace(' (€)', '').toLowerCase()]));
const nomeCampo = (campo) => NOME_CAMPO[campo] || campo.replace(/_/g, ' ');

const RigaLettura = ({ lettura, link }) => {
  const [dettagli, setDettagli] = useState(false);
  const sito = dominio(link || '') || (lettura.portale ? `${lettura.portale}.it` : '');
  const giorno = formattaRoma(lettura.quando, { day: '2-digit', month: '2-digit' });
  const ora = formattaRoma(lettura.quando, { hour: '2-digit', minute: '2-digit' });
  const campi = lettura.campi || [];
  const confidenza = lettura.confidenza || {};
  const fallita = lettura.stato === 'fallito' || Boolean(lettura.errore);
  return (
    <div className="riga-lettura" data-testid="riga-lettura">
      <span>
        Letto dall'estrattore{giorno ? ` il ${giorno}` : ''}{ora ? ` alle ${ora}` : ''}{sito ? ` da ${sito}` : ''}
      </span>
      <button type="button" className="link" aria-expanded={dettagli} onClick={() => setDettagli(!dettagli)}>dettagli lettura</button>
      {dettagli && (
        <div className="dett" data-testid="dettagli-lettura">
          {campi.length > 0 ? (
            <p>Dall'annuncio: {campi.map((c) => `${nomeCampo(c)}${confidenza[c] != null ? ` ${Math.round(confidenza[c] * 100)}%` : ''}`).join(' · ')}</p>
          ) : <p>L'annuncio non ha dato nessun dato: sono tutti scritti da noi.</p>}
          {fallita && <p className="no">Lettura non riuscita{lettura.errore ? ` · ${lettura.errore}` : ''}</p>}
        </div>
      )}
    </div>
  );
};

const SchedaImmobile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userType } = useAuth();
  const isAdmin = userType === 'admin';
  const [stato, setStato] = useState('carica');  // carica | ok | vietato | nontrovato | errore
  const [dati, setDati] = useState(null);
  const [aperto, setAperto] = useState(null);  // 'esito' | 'visita' | 'compila' | 'elimina' | 'gruppo-esito' | 'gruppo-visite'

  const carica = useCallback(async () => {
    const [immobile, staff, visite, storico, stima, doppie] = await Promise.allSettled([
      axios.get(`${API}/immobili/${id}`), axios.get(`${API}/immobili/${id}/staff`), axios.get(`${API}/immobili/${id}/visite`),
      axios.get(`${API}/immobili/${id}/storico`), axios.get(`${API}/immobili/${id}/stima-mutuo`),
      axios.get(`${API}/immobili/${id}/doppioni`),
    ]);
    if (immobile.status === 'rejected') {
      const codice = immobile.reason?.response?.status;
      setStato(codice === 403 ? 'vietato' : codice === 404 ? 'nontrovato' : 'errore');
      return;
    }
    setDati({
      immobile: immobile.value.data,
      staff: staff.status === 'fulfilled' ? staff.value.data : { note: [], agenzia: null },
      visite: visite.status === 'fulfilled' ? visite.value.data || [] : [],
      storico: storico.status === 'fulfilled' ? storico.value.data || [] : [],
      stima: stima.status === 'fulfilled' ? stima.value.data : null,
      doppioni: doppie.status === 'fulfilled' ? doppie.value.data?.doppioni || [] : [],
    });
    setStato('ok');
  }, [id]);
  useEffect(() => { carica(); }, [carica]);
  const fatto = () => { setAperto(null); carica(); };

  if (stato !== 'ok') {
    return (
      <main className="st wrap scheda-imm-staff">
        <button type="button" className="back link-back" onClick={() => navigate(-1)}><Icona nome="indietro" />Indietro</button>
        <p className="vuoto" role="status">
          {stato === 'carica' && 'Carico la scheda…'}
          {stato === 'vietato' && 'Questo immobile è di un cliente che non seguite.'}
          {stato === 'nontrovato' && 'Immobile non trovato: forse è stato eliminato (Impostazioni → Cestino).'}
          {stato === 'errore' && 'Non riesco a caricare la scheda. Riprovate tra poco.'}
        </p>
      </main>
    );
  }

  const { immobile: i, staff: s, visite, storico, stima, doppioni: doppie } = dati;
  const c = s.cliente;
  const spento = i.stato_attivita === 'scartato';
  const daCompilare = i.stato_elaborazione === 'da_compilare';
  const [, nomeStato, coloreStato] = statoImmobile(i.stato_attivita);
  const prezzo = prezzoNumero(i);
  const mq = prezzoMq(i);
  const indicazioniLink = linkIndicazioni(i);
  const tel = linkChiama(s.agenzia?.telefono);
  const riga = rigaVisita(i);
  const eAsta = s.asta ?? i.asta === true;  // asta giudiziaria: resta, ma si dice (backend/aste.py)
  const aggiunto = leggiDataUtc(i.data_aggiunta);
  const voce = { id: `tel:${i.id}`, tipo: 'telefonata', immobile_id: i.id, agenzia_id: i.agenzia_id, cliente_id: c?.id, cliente_breve: c?.nome, tentativi: 0 };
  const cliente = c ? { id: c.id, nome: c.nome } : null;
  // le altre case attive dello stesso cliente con la stessa agenzia (backend/agenzia_case.py)
  const altre = s.altre_case || { quante: 0, case: [], gruppo: [] };
  const riprova = async () => {
    try { await axios.post(`${API}/admin/estrazioni/${i.id}/riprova`); toast.success("Rimesso in coda: l'estrattore lo rilegge"); carica(); } catch (e) {
      toast.error(messaggioErrore(e));
    }
  };
  const sottoPrezzo = [formatEuroMq(mq), s.proposta?.prezzo_offerto && `proposta ${euro(s.proposta.prezzo_offerto)}`].filter(Boolean).join(' · ');
  // La stessa casa presa da un altro portale: i link uniti alla scheda e, se non è ancora unita, l'avviso
  const altri = altriPortali(i);
  const unisci = async (altroId) => {
    try {
      // resta la scheda del portale più completo: il server dice quale, e ci si va
      const r = await axios.post(`${API}/immobili/${i.id}/unisci`, { immobile_id: altroId });
      toast.success('Unite: una casa sola');
      if (r.data?.immobile_id && r.data.immobile_id !== i.id) navigate(`/immobili/${r.data.immobile_id}`);
      else carica();
    } catch (e) { toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Non riuscito'); }
  };

  return (
    <main className="st wrap scheda-imm-staff" data-testid="scheda-immobile-staff">
      {c && (
        <div className="imm-cliente" data-testid="testa-cliente">
          <Link className="back" to={`/admin/clienti/${c.id}?evidenzia=${encodeURIComponent(i.id)}#immobili`}><Icona nome="indietro" />Immobili di {c.nome}</Link>
          <div className="riga">
            <div className="av">{c.iniziale}</div>
            <div style={{ minWidth: 0 }}>
              <div className="k">Immobile{s.numero ? ` n. ${s.numero} di ${s.di}` : ''} · {ORIGINE[i.inserito_da_tipo] || 'aggiunto'}{aggiunto ? ` ${ilGiorno(giornoEMese(aggiunto))}` : ''}</div>
              <Link className="nome" to={`/admin/clienti/${c.id}`}>{c.nome_intero}</Link>
              <div className="meta">
                <span className="tag t-o">fase {c.fase.nome}</span>
                {c.segue && <span className="who"><Iniziale persona={c.segue} />segue {c.segue.nome}</span>}
                {c.telefono && <a href={linkChiama(c.telefono) || '#'}>{c.telefono}</a>}
              </div>
            </div>
            <div className="stato-imm" data-testid="stato-attivita">
              <CambiaStato immobile={i} onFatto={carica} isAdmin={isAdmin} />
            </div>
          </div>
        </div>
      )}

      <GalleriaFoto foto={fotoImmobile(i)} titolo={titoloImmobile(i)} className="gf-admin" />

      <div className="imm-sh">
        <div style={{ minWidth: 0 }}>
          <span className="tag" style={{ color: coloreStato, background: 'rgba(243,237,226,.05)' }}>{nomeStato}</span>
          {eAsta && <span className="tag t-r" data-testid="tag-asta" style={{ marginLeft: '.4rem' }}>{s.asta_etichetta || 'Asta'}</span>}
          <h1>{titoloImmobile(i)}</h1>
          {indirizzoCompleto(i) && <div className="dove">{indirizzoCompleto(i)}{i.zona ? ` · ${i.zona}` : ''}</div>}
          <DatiImmobile immobile={i} />
          <Dintorni immobile={i} />
          {s.lettura && <RigaLettura lettura={s.lettura} link={i.link} />}
          {eAsta && s.asta_avviso && <div className="avviso-imm w" data-testid="avviso-asta">{s.asta_avviso}</div>}
          {daCompilare && <div className="avviso-imm w">L'estrattore non l'ha letto{i.estrazione_errore ? ` · ${i.estrazione_errore}` : ''}: dati da compilare</div>}
          {/* la stessa casa su più portali è una proposta sola (backend/doppioni.py) */}
          {altri.length > 0 && (
            <div className="dove" data-testid="anche-su">
              Anche su {altri.map((p, n) => (
                <React.Fragment key={p.link}>
                  {n > 0 && ' · '}
                  <a href={ensureAbsoluteUrl(p.link)} target="_blank" rel="noopener noreferrer">{p.nome}</a>
                </React.Fragment>
              ))}
            </div>
          )}
          {(doppie || []).map((d) => (
            <div className="avviso-imm w" key={d.immobile_id} data-testid="avviso-doppione">
              Questa casa è già presente da {dominio(d.link) || 'un altro sito'} ({d.motivo}):{' '}
              <Link to={`/immobili/${d.immobile_id}`}>{d.titolo || "l'altra scheda"}</Link>
              {isAdmin && <button type="button" className="btn sm p" style={{ marginLeft: '.6rem' }} onClick={() => unisci(d.immobile_id)}>Unisci</button>}
            </div>
          ))}
          {venduto(i) && <div className="avviso-imm no">{testoVenduto(i)}</div>}
          {ritirato(i) && !venduto(i) && <div className="avviso-imm no">{testoRitirato(i)}</div>}
          {spento && <div className="avviso-imm">Scartato{i.scartato_dal_cliente ? ' dal cliente' : ''}{i.motivo_scarto ? ` · ${(MOTIVI_SCARTO.find((m) => m[0] === i.motivo_scarto) || [])[1] || i.motivo_scarto}` : ''}{i.note_scarto ? ` · “${i.note_scarto}”` : ''}</div>}
          {i.visita_richiesta && <div className="avviso-imm ok">Il cliente ha chiesto la visita{i.visita_richiesta.nota ? ` · “${i.visita_richiesta.nota}”` : ''}</div>}
          {/* Il link c'è, ma non è di un portale che l'estrattore sa leggere (un link di condivisione di Google, un
              accorciatore, il sito dell'agenzia): la casa non verrà letta mai, e prima non lo diceva nessuno — questa
              era in lista da maggio con un share.google, senza un dato dentro (backend/server.py) */}
          {i.link_non_leggibile && (
            <div className="avviso-imm w" data-testid="avviso-link-non-leggibile">
              Link non leggibile: incollate il link del portale (immobiliare.it, idealista.it, casa.it), se no l'estrattore non riuscirà a leggere questa casa.
            </div>
          )}
          {i.descrizione && <details className="descr-imm"><summary>Descrizione dell'annuncio</summary><p>{i.descrizione}</p></details>}
        </div>
        <div className="destra">
          <div className="pr">{prezzo ? euro(prezzo) : 'Prezzo da capire'}{sottoPrezzo && <small>{sottoPrezzo}</small>}</div>
          <div className="imp-azioni" style={{ justifyContent: 'flex-end' }}>
            {i.link && <a className="btn sm" href={ensureAbsoluteUrl(i.link)} target="_blank" rel="noopener noreferrer">Annuncio ↗</a>}
            {indicazioniLink && <a className="btn sm" href={indicazioniLink} target="_blank" rel="noopener noreferrer">Indicazioni</a>}
          </div>
        </div>
      </div>

      <section className="card imm-azioni" data-testid="azioni-immobile">
        {riga && (
          <div className={`riga-visita ${riga.tipo}`} data-testid="riga-visita">
            <Icona nome={riga.tipo === 'prossima' ? 'calendario' : 'si'} /><b>{riga.testo}</b>
            {riga.tipo === 'prossima' && (
              <span className="az">
                <a href={indicazioni(i)} target="_blank" rel="noopener noreferrer">Indicazioni</a>
                <AzioniVisita visita={visitaDa(riga.visita, i.id)} cliente={cliente} onFatto={carica} piccoli />
              </span>
            )}
          </div>
        )}
        <div className="imp-azioni" style={{ marginTop: riga ? '.8rem' : 0 }}>
          {!spento && i.agenzia_id && <button type="button" className={`btn sm${i.visita_richiesta ? ' p' : ''}`} aria-expanded={aperto === 'esito'}
            onClick={() => setAperto(aperto === 'esito' ? null : 'esito')}>Esito telefonata</button>}
          {!spento && tel && <a className="btn sm" href={tel}>Chiama l'agenzia</a>}
          {!spento && c && !i.prossima_visita && <button type="button" className={`btn sm${i.visita_richiesta ? ' p' : ''}`} onClick={() => setAperto('visita')}>Fissa visita</button>}
          {daCompilare && isAdmin && <button type="button" className="btn sm p" onClick={() => setAperto('compila')}>Compila dati</button>}
          {daCompilare && isAdmin && i.estrazione_stato === 'fallito' && <button type="button" className="btn sm" onClick={riprova}>Riprova la lettura</button>}
          {isAdmin && <button type="button" className="btn sm rosso-vuoto" style={{ marginLeft: 'auto' }} onClick={() => setAperto('elimina')}>Elimina</button>}
        </div>
        {/* l'esito di questa casa sola: se il cliente ne ha altre con la stessa agenzia, il pannello lo chiede
            con le spunte («ha altre 2 case per Vasile: vuoi dare l'esito anche per quelle?») */}
        {aperto === 'esito' && <EsitoTelefonata voce={voce} isAdmin={isAdmin} nomeCliente={c?.nome} altreCase={altre} onFatto={fatto} />}
      </section>

      <div className="g2">
        <div className="stack">
          <ParereStaff immobile={i} onFatto={carica} />
          <VisiteImmobile immobile={i} visite={visite} cliente={cliente} onFatto={carica} />
          <PropostaImmobile proposta={s.proposta} clienteId={c?.id} />
          <StoriaStaff storico={storico} />
        </div>
        <div className="stack">
          <AgenziaImmobile immobile={i} agenzia={s.agenzia} commento={s.commento_agenzia} isAdmin={isAdmin} onFatto={carica} />
          {/* «Questo cliente ha altre 2 case con Ermes»: una telefonata sola e, volendo, le visite in fila
              (backend/agenzia_case.py, docs/piu-case-agenzia.md) */}
          {altre.quante > 0 && (
            <section className="card r altre-case-imm" data-testid="altre-case">
              <b>Questo cliente ha {altre.quante === 1 ? "un'altra casa" : `altre ${altre.quante} case`} con {altre.etichetta}</b>
              <p className="dove">
                {altre.case.map((k, n) => (
                  <React.Fragment key={k.id}>
                    {n > 0 && ' · '}
                    <Link to={linkImmobileInScheda(c?.id, k.id)}>{k.indirizzo || k.titolo || 'casa senza indirizzo'}</Link>
                  </React.Fragment>
                ))}
              </p>
              <div className="imp-azioni">
                <button type="button" className="btn sm" onClick={() => setAperto(aperto === 'gruppo-esito' ? null : 'gruppo-esito')}
                  aria-expanded={aperto === 'gruppo-esito'}>Chiama per {altre.gruppo.length} case</button>
                {c && <button type="button" className="btn sm" onClick={() => setAperto('gruppo-visite')}>Visite in fila</button>}
              </div>
              {aperto === 'gruppo-esito' && (
                <EsitoTelefonata voce={voce} isAdmin={isAdmin} nomeCliente={c?.nome} case={altre.gruppo} nomeAgenzia={altre.etichetta} onFatto={fatto} />
              )}
            </section>
          )}
          <NoteImmobile immobile={i} note={s.note || []} onFatto={carica} />
          <PrezzoZonaImmobile immobile={i} />
          <SeLaCompra stima={stima} nome={c?.nome} />
          {/* «È un'asta» cambia anche l'etichetta «Asta» e l'avviso in cima: la scheda si rilegge da sola */}
          <ProponiAdAltri immobile={i} onFatto={carica} />
        </div>
      </div>

      {aperto === 'visita' && c && <FissaVisita clienteId={c.id} clienteNome={c.nome_intero || c.nome} immobileId={i.id} onChiudi={() => setAperto(null)} onFatto={fatto} />}
      {aperto === 'gruppo-visite' && c && <FissaVisita clienteId={c.id} clienteNome={c.nome_intero || c.nome} immobileId={i.id}
        caseInSequenza={altre.gruppo} onChiudi={() => setAperto(null)} onFatto={fatto} />}
      {aperto === 'compila' && <CompilaDati immobile={i} onChiudi={() => setAperto(null)} onCompilato={fatto} />}
      {aperto === 'elimina' && <Eliminazione tipo="immobile" id={i.id} onChiudi={() => setAperto(null)}
        onEliminato={() => navigate(c ? `/admin/clienti/${c.id}#immobili` : '/admin/clienti')} />}
    </main>
  );
};

export default SchedaImmobile;
